import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Shield,
  Users,
  Settings,
  Key,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatDisplayDate } from "@/lib/utils";
import { Role, Permission } from "@/types/auth";
import {
  useRoles,
  usePermissions,
  useDeleteRole,
} from "@/api-services/hooks/useRoles";
import { RoleDTO } from "@/api-services/hooks/adminTypes";
import {
  useConfirmDialog,
  createDeleteConfirm,
} from "@/components/ui/confirm-dialog";
import DataTablePagination from "@/components/ui/data-table-pagination";

const RolesPage: React.FC = () => {
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingRole, setViewingRole] = useState<Role | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const navigate = useNavigate();

  // Memoize callbacks to prevent infinite re-renders
  const handleRolesError = useCallback((error: Error) => {
    toast.error(`Failed to load roles: ${error.message}`);
  }, []);

  const handlePermissionsError = useCallback((error: Error) => {
    toast.error(`Failed to load permissions: ${error.message}`);
  }, []);

  // Memoize pageable object to prevent unnecessary re-renders
  const pageable = useMemo(() => ({
    page: currentPage - 1, // API uses 0-based indexing
    size: itemsPerPage,
  }), [currentPage, itemsPerPage]);

  // API hooks - removed search parameter since API doesn't support it
  // We'll do client-side filtering instead
  const rolesQuery = useRoles({
    pageable,
    onError: handleRolesError,
  });

  const permissionsQuery = usePermissions({
    onError: handlePermissionsError,
  });

  const deleteRoleMutation = useDeleteRole({
    onSuccess: () => {
      toast.success("Role deleted successfully!");
      rolesQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to delete role: ${error.message}`);
    },
  });

  // Convert API Role to UI Role format
  const convertApiRoleToUIRole = (apiRole: any): Role => {
    // Extract roleId from API response - prioritize roleId field
    const apiRoleId = apiRole.roleId ?? apiRole.id;
    const roleIdValue = apiRoleId !== undefined && apiRoleId !== null ? String(apiRoleId) : '';

    const role: Role = {
      id: roleIdValue,
      name: apiRole.roleName || apiRole.name || '',
      description: apiRole.roleDescription || apiRole.description || '',
      isSystemRole: apiRole.isSystemRole || false,
      permissions: Array.isArray(apiRole.permissions)
        ? apiRole.permissions.map((p: any) =>
            typeof p === 'string'
              ? { id: '', name: p, resource: '', action: '', description: '' }
              : p
          )
        : [], // Handle null/undefined permissions
      createdAt: apiRole.createdAt || new Date().toISOString(),
      updatedAt: apiRole.updatedAt || new Date().toISOString(),
    };

    // Store additional API data that's not in the Role interface
    // IMPORTANT: Store the original roleId as number for edit/delete operations
    (role as any).roleId = apiRoleId !== undefined && apiRoleId !== null 
      ? (typeof apiRoleId === 'number' ? apiRoleId : parseInt(String(apiRoleId), 10))
      : null;
    (role as any).status = apiRole.status || '';
    (role as any).rolePermissions = apiRole.rolePermissions || '';
    (role as any).restricted = Array.isArray(apiRole.restricted) ? apiRole.restricted : [];
    (role as any).isDeletedValue = apiRole.isDeletedValue || false;
    (role as any).remarks = apiRole.remarks || '';

    return role;
  };

  // Convert API Permission to UI Permission format
  const convertApiPermissionToUIPermission = (apiPermission: any): Permission => {
    return {
      id: String(apiPermission.id),
      name: apiPermission.name,
      resource: apiPermission.resource,
      action: apiPermission.action,
      description: apiPermission.description,
      createdAt: apiPermission.createdAt || new Date().toISOString(),
      updatedAt: apiPermission.updatedAt || new Date().toISOString(),
    };
  };

  // Memoize roles conversion to prevent unnecessary re-renders
  const roles: Role[] = useMemo(() => {
    const rolesData = rolesQuery.data?.content || [];
    const converted = rolesData.map(convertApiRoleToUIRole);
    return converted;
  }, [rolesQuery.data]);

  // Memoize permissions conversion to prevent unnecessary re-renders
  const permissions: Permission[] = useMemo(() => {
    const permissionsData = permissionsQuery.data || [];
    return permissionsData.map(convertApiPermissionToUIPermission);
  }, [permissionsQuery.data]);

  const [filteredRoles, setFilteredRoles] = useState<Role[]>([]);

  const { confirm, ConfirmDialog } = useConfirmDialog();

  // Filter roles based on search and status (client-side filtering)
  useEffect(() => {
    let filtered = roles;

    // Client-side search filtering
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((role) => {
        const nameMatch = role.name?.toLowerCase().includes(searchLower);
        const descriptionMatch = role.description?.toLowerCase().includes(searchLower);
        return nameMatch || descriptionMatch;
      });
    }

    // Status filter (system vs custom)
    if (statusFilter !== "all") {
      filtered = filtered.filter((role) =>
        statusFilter === "system" ? role.isSystemRole : !role.isSystemRole
      );
    }

    setFilteredRoles(filtered);
  }, [roles, statusFilter, searchTerm]);

  // Reset to first page when status filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchTerm]);

  // Pagination calculations
  const totalItems = filteredRoles.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRoles = filteredRoles.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  const handleView = (role: Role) => {
    setViewingRole(role);
    setIsViewDialogOpen(true);
  };

  const handleEdit = (role: Role) => {
    const roleData = role as any;
    const roleId = roleData.roleId ?? role.id;
    navigate(`/admin/users/roles/edit/${roleId}`);
  };

  const handleDelete = (role: Role) => {
    if (role.isSystemRole) {
      toast.error("Cannot delete system roles");
      return;
    }

    const roleName = role.name;
    const roleData = role as any;
    
    // Prioritize the stored roleId from API response, fallback to parsing id field
    let roleId: number;
    if (roleData.roleId !== undefined && roleData.roleId !== null) {
      roleId = typeof roleData.roleId === 'number' 
        ? roleData.roleId 
        : parseInt(String(roleData.roleId), 10);
    } else if (typeof role.id === "string") {
      roleId = parseInt(role.id, 10);
    } else {
      roleId = role.id as number;
    }

    confirm(
      createDeleteConfirm(
        "Role",
        async () => {
          await deleteRoleMutation.mutate(roleId as number);
        },
        `Are you sure you want to delete ${roleName}? This will remove the role from all users who currently have it assigned. This action cannot be undone.`
      )
    );
  };

  const getPermissionsByResource = useCallback(() => {
    const grouped = permissions.reduce((acc, permission) => {
      if (!acc[permission.resource]) {
        acc[permission.resource] = [];
      }
      acc[permission.resource].push(permission);
      return acc;
    }, {} as Record<string, Permission[]>);

    return Object.entries(grouped);
  }, [permissions]);

  const getPermissionIcon = (action: string) => {
    switch (action) {
      case "create":
        return <Plus className="h-4 w-4" />;
      case "read":
        return <Users className="h-4 w-4" />;
      case "update":
        return <Edit className="h-4 w-4" />;
      case "delete":
        return <Trash2 className="h-4 w-4" />;
      case "manage":
        return <Settings className="h-4 w-4" />;
      default:
        return <Key className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Roles & Permissions</h1>
          <p className="text-muted-foreground">
            Manage user roles and their associated permissions
          </p>
        </div>
        <Button onClick={() => navigate("/admin/users/roles/add")}>
          <Plus className="w-4 h-4 mr-2" />
          Add Role
        </Button>
      </div>

      {/* View Role Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                {viewingRole?.name}
              </DialogTitle>
              <DialogDescription>
                View detailed information about this role and its associated permissions.
              </DialogDescription>
            </DialogHeader>

            {viewingRole && (
              <div className="space-y-6">
                {/* Role Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Role Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Role ID:</span>
                        <span className="font-mono text-sm">{viewingRole.id}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Status:</span>
                        {(() => {
                          const status = (viewingRole as any).status?.toLowerCase() || '';
                          if (!status) {
                            return <Badge variant="secondary">Not Set</Badge>;
                          }
                          
                          const statusConfig = {
                            active: { variant: 'default', className: 'bg-green-500 text-white hover:bg-green-600' },
                            inactive: { variant: 'secondary', className: 'bg-gray-500 text-white hover:bg-gray-600' },
                            pending: { variant: 'secondary', className: 'bg-yellow-500 text-white hover:bg-yellow-600' },
                          }[status] || { variant: 'secondary', className: '' };
                          
                          return (
                            <Badge variant={statusConfig.variant as any} className={statusConfig.className}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </Badge>
                          );
                        })()}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Type:</span>
                        <Badge variant={viewingRole.isSystemRole ? "secondary" : "default"}>
                          {viewingRole.isSystemRole ? "Systems Role" : "Custom Role"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Permissions Count:</span>
                        <Badge variant="outline">{viewingRole.permissions.length} permissions</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Created:</span>
                        <span className="text-sm">{formatDisplayDate(viewingRole.createdAt)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Last Updated:</span>
                        <span className="text-sm">{formatDisplayDate(viewingRole.updatedAt)}</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Description</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {viewingRole.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Permissions by Resource */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Key className="w-5 h-5" />
                      Associated Permissions
                    </CardTitle>
                    <CardDescription>
                      This role has access to {viewingRole.permissions.length} permissions across different resources.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {Object.entries(
                        viewingRole.permissions.reduce((acc, permission) => {
                          if (!acc[permission.resource]) {
                            acc[permission.resource] = [];
                          }
                          acc[permission.resource].push(permission);
                          return acc;
                        }, {} as Record<string, Permission[]>)
                      ).map(([resource, resourcePermissions]) => (
                        <div key={resource} className="border rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-4">
                            <Shield className="h-5 w-5 text-primary" />
                            <h4 className="font-semibold text-lg">
                              {resource.charAt(0).toUpperCase() + resource.slice(1)}
                            </h4>
                            <Badge variant="secondary" className="ml-2">
                              {resourcePermissions.length} permissions
                            </Badge>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {resourcePermissions.map((permission) => (
                              <div
                                key={permission.id}
                                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded">
                                    {getPermissionIcon(permission.action)}
                                  </div>
                                  <div>
                                    <div className="font-medium text-sm">{permission.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                      {permission.description}
                                    </div>
                                  </div>
                                </div>
                                <Badge variant="outline" className="capitalize">
                                  {permission.action}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {viewingRole.permissions.length === 0 && (
                      <div className="text-center py-8">
                        <Key className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h4 className="text-lg font-medium mb-2">No Permissions Assigned</h4>
                        <p className="text-muted-foreground">
                          This role doesn't have any permissions assigned to it.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setIsViewDialogOpen(false)}
                  >
                    Close
                  </Button>
                  {!viewingRole.isSystemRole && (
                    <Button
                      onClick={() => {
                        setIsViewDialogOpen(false);
                        handleEdit(viewingRole);
                      }}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Role
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Roles</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{roles.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Systems Roles</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {roles.filter((r) => r.isSystemRole).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custom Roles</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {roles.filter((r) => !r.isSystemRole).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Permissions
            </CardTitle>
            <Key className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{permissions.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search roles by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="system">Systems Roles</SelectItem>
                <SelectItem value="custom">Custom Roles</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Roles Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Roles (
            {rolesQuery.loading 
              ? "..." 
              : searchTerm.trim() || statusFilter !== "all"
                ? `${filteredRoles.length} filtered`
                : roles.length}
            )
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rolesQuery.loading || permissionsQuery.loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2"></div>
                      <p className="text-muted-foreground">Loading roles...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : paginatedRoles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium">No roles found</h3>
                    <p className="text-muted-foreground">
                      {searchTerm || statusFilter !== "all"
                        ? "Try adjusting your filters"
                        : "Get started by creating your first role"}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRoles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Shield className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">{role.name}</div>
                          {role.isSystemRole && (
                            <Badge variant="secondary" className="text-xs">
                              Systems
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground max-w-xs">
                        {role.description}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <Badge variant="outline">
                          {role.permissions.length} permissions
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const status = (role as any).status?.toLowerCase() || '';
                        if (!status) {
                          return <Badge variant="secondary">Not Set</Badge>;
                        }
                        
                        const statusConfig = {
                          active: { variant: 'default', className: 'bg-green-500 text-white hover:bg-green-600' },
                          inactive: { variant: 'secondary', className: 'bg-gray-500 text-white hover:bg-gray-600' },
                          pending: { variant: 'secondary', className: 'bg-yellow-500 text-white hover:bg-yellow-600' },
                        }[status] || { variant: 'secondary', className: '' };
                        
                        return (
                          <Badge variant={statusConfig.variant as any} className={statusConfig.className}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </Badge>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={role.isSystemRole ? "secondary" : "default"}
                      >
                        {role.isSystemRole ? "Systems" : "Custom"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        {formatDisplayDate(role.createdAt)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleView(role)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(role)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(role)}
                          disabled={deleteRoleMutation.loading || role.isSystemRole}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {!rolesQuery.loading && !permissionsQuery.loading && filteredRoles.length > 0 && (
            <DataTablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              className="mt-4"
            />
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <ConfirmDialog />
    </div>
  );
};

export default RolesPage;
